import { css } from 'styled-components';
import { useEffect } from 'react';
import {
  Box,
  Lozenge,
  Text,
  CompassRose,
  CastleTurret,
  StarFilled,
  PushPass,
  Ranking,
  Faucet,
  SquaresFour,
  DotsThree,
  CaretLeftCircle,
  Cross,
  SquadsIcon,
  Cult,
  Twitter,
  Discord,
  DiscordWhite,
  Link,
} from '../../blocks';
import type { IconProps } from '../../blocks/icons/Icons.types';
import { device } from '../../config/globals';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useNavigate, useLocation } from 'react-router-dom';
import { PushUniversalAccountButton, usePushWalletContext } from '@pushchain/ui-kit';
import { walletToFullCAIP10 } from '../../helpers/web3helper';
import { useGetUserCultStatus } from '../../queries';
import { useActivityContext } from '../../context/activityContext';
import { FLAGS } from '../../config/flags';

type MenuItem = {
  id: string;
  icon: React.ComponentType<IconProps>;
  label: string;
  badge?: {
    text: string;
    icon?: React.ComponentType<IconProps>;
  };
  onClick?: () => void;
  route?: string;
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar = ({ isOpen = false, onClose }: SidebarProps) => {
  const isLaptop = useMediaQuery(device.laptopL);
  const navigate = useNavigate();
  const location = useLocation();

  const { isVerified } = useActivityContext();
  const { universalAccount } = usePushWalletContext('wallet1');

  const isWalletConnected = Boolean(universalAccount?.address);

  const caip10WalletAddress = walletToFullCAIP10(
    universalAccount?.address as string,
    universalAccount?.chain,
  );

  const { data: userCultStatus } = useGetUserCultStatus({
    wallet: caip10WalletAddress
  });

  const isCultUser = userCultStatus?.data?.isCultMember;

  const showCultDashboard = isWalletConnected && isCultUser;

  const getActiveItemId = (): string => {
    const path = location.pathname;

    if (path === '/') return 'season3';
    if (path === '/rewards' || path === '/rewards/') return 'discover';
    if (path.startsWith('/rewards/pushpass')) return 'push-pass';
    if (path.startsWith('/rewards/squads')) return 'squads';
    if (path.startsWith('/rewards/leaderboard')) return 'leaderboards';
    if (path.startsWith('/rewards/pre-launch')) return 'pre-launch';
    if (path.startsWith('/cult/leaderboard')) return 'cult-leaderboard';
    if (path.startsWith('/cult')) return 'cult';

    return 'discover';
  };

  const activeItemId = getActiveItemId();

  useEffect(() => {
    if (isOpen && isLaptop) {
      const handleClickOutside = (e: MouseEvent) => {
        const sidebar = document.getElementById('sidebar');
        if (sidebar && !sidebar.contains(e.target as Node)) {
          onClose?.();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, isLaptop, onClose]);

  const topMenuItems: MenuItem[] = [

    ...(FLAGS.SEASON_THREE ? [
      {
        id: 'discover',
        icon: CompassRose,
        label: 'Discover',
        route: '/rewards'
      },
      {
        id: 'push-pass',
        icon: PushPass,
        label: 'Push Pass',
        route: '/rewards/pushpass'
      },
      {
        id: 'squads',
        icon: SquadsIcon,
        label: 'Invites/Squads',
        route: '/rewards/squads'
      },
      {
        id: 'leaderboards',
        icon: Ranking,
        label: 'Leaderboards',
        route: '/rewards/leaderboard'
      },
      ...(showCultDashboard ? [{
        id: 'cult',
        icon: Cult,
        label: 'Cult',
        route: '/cult'
      },
      {
        id: 'cult-leaderboard',
        icon: Ranking,
        label: 'Cult Leaderboards',
        route: '/cult/leaderboard',
      }] : []),
    ] : []),

    ...(FLAGS.CULT ? [
      {
        id: 'season3',
        icon: CompassRose,
        label: 'Season 3',
        route: '/'
      },

      // ...(showCultDashboard ? [{
      //   id: 'cult-leaderboard',
      //   icon: Ranking,
      //   label: 'Cult Leaderboards',
      //   route: '/cult/leaderboard',
      // }] : []),
    ] : []),
  ];

  const bottomMenuItems: MenuItem[] = [
    // {
    //   id: 'faucet',
    //   icon: Faucet,
    //   label: 'PC Token Faucet',
    //   route: 'https://faucet.push.org'
    // },
    // {
    //   id: 'ecosystem',
    //   icon: SquaresFour,
    //   label: 'Ecosystem Apps',
    //   route: 'https://push.org/ecosystem'
    // },
  ];

  const getItemStyles = (isActive: boolean) => css`
    cursor: pointer;
    transition: all 0.2s ease;

    ${isActive
      ? css`
          backdrop-filter: blur(20px);
          box-shadow: inset 1.86px 1.73px 6px rgba(255, 255, 255, 0.15),
            inset 2.79px -8px 12px rgba(255, 255, 255, 0.15);
          background-color: rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(171, 70, 248, 0.40);
        `
      : css`
          border: 1px solid transparent;
          &:hover {
            background-color: rgba(255, 255, 255, 0.05);
          }
        `}
  `;


  const handleItemClick = (itemId: string, onClick?: () => void, route?: string) => {
    if (route) {
      if (route.startsWith('http')) {
        window.open(route, '_blank');
      } else {
        navigate(route);
      }
    }
    onClick?.();
    if (isLaptop) {
      onClose?.();
    }
  };

  return (
    <>
      {isLaptop && isOpen && (
        <Box
          css={css`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 999;
          `}
          onClick={onClose}
        />
      )}

      <Box
        id="sidebar"
        display="flex"
        flexDirection="column"
        width="284px"
        padding="spacing-sm"
        css={css`
          height: 100%;
          position: relative;
          box-sizing: border-box;

          @media ${device.laptopL} {
            position: fixed;
            top: 0;
            left: 0;
            height: 100vh;
            background-color: #1a1a1a;
            transform: translateX(${isOpen ? '0' : '-100%'});
            transition: transform 0.3s ease-in-out;
            z-index: 1000;
            overflow-y: auto;
          }
        `}
      >
        {isLaptop && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="spacing-none spacing-none spacing-xs spacing-none"
          >
            <PushUniversalAccountButton uid='wallet1' />
            <Box cursor="pointer" onClick={onClose}>
              <Cross size={24} color="icon-primary" />
            </Box>
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap="spacing-xxs">
          {topMenuItems.map((item) => (
            <Box
              key={item.id}
              display="flex"
              alignItems="center"
              gap="spacing-xxs"
              padding="spacing-xxs spacing-sm"
              borderRadius="radius-xs"
              css={getItemStyles(activeItemId === item.id)}
              onClick={() => handleItemClick(item.id, item.onClick, item.route)}
            >
              <item.icon size={32} color="icon-brand-medium" />
              <Text
                variant={
                  activeItemId === item.id ? 'h5-semibold' : 'h5-regular'
                }
                color="text-primary"
              >
                {item.label}
              </Text>
              {item.badge && (
                <Lozenge
                  size="small"
                  icon={item.badge.icon && <item.badge.icon size={10} />}
                  css={css`
                    background-color: #f7cbff;
                    border-radius: 4px;
                    padding: 2px 6px;
                  `}
                >
                  <Text
                    variant="c-bold"
                    css={css`
                      color: #aa30be;
                      font-size: 10px;
                      text-transform: uppercase;
                    `}
                  >
                    {item.badge.text}
                  </Text>
                </Lozenge>
              )}
            </Box>
          ))}
        </Box>

        <Box css={css`flex: 1;`} />

        <Box display="flex" flexDirection="column" gap="spacing-xxs">
          {bottomMenuItems.map((item) => (
            <Box
              key={item.id}
              display="flex"
              alignItems="center"
              gap="spacing-xxs"
              padding="spacing-xxs spacing-sm"
              borderRadius="radius-xs"
              css={getItemStyles(activeItemId === item.id)}
              onClick={() => handleItemClick(item.id, item.onClick, item.route)}
            >
              <item.icon size={32} color="icon-brand-medium" />
              <Text
                variant={
                  activeItemId === item.id ? 'h5-semibold' : 'h5-regular'
                }
                color="text-primary"
              >
                {item.label}
              </Text>
            </Box>
          ))}


          <Box
            display="flex"
            alignItems="center"
            gap="spacing-md"
          >
            <a href="https://x.com/PushChain" target="_blank" rel="noopener noreferrer">
              <Twitter width={30} height={30} />
            </a>
            <a href="https://discord.com/invite/pushchain" target="_blank" rel="noopener noreferrer">
              <DiscordWhite width={38} />
            </a>
          </Box>

          <Box display='flex' flexDirection='row' gap='spacing-sm'>
            <Link to='https://push.org/tos' title='Terms of Service' target='_blank'>
              Terms of Service
            </Link>

            <Link to='https://push.org/privacy' title='Privacy Policy' target='_blank'>
              Privacy Policy
            </Link>
          </Box>
        </Box>
      </Box>
    </>
  );
};
